import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { 
  Car, Phone, Mail, MapPin, Calendar as CalendarIcon, Clock, 
  CheckCircle, Wrench, Sparkles, Wind, PaintBucket, Settings,
  Loader2, ArrowRight, User, Fuel, Share2, Copy
} from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SERVICE_ICONS = {
  general: Wrench,
  denting: PaintBucket,
  ac: Wind,
  vas: Sparkles,
  other: Settings
};

const SERVICE_COLORS = {
  general: 'bg-blue-500',
  denting: 'bg-purple-500',
  ac: 'bg-cyan-500',
  vas: 'bg-pink-500',
  other: 'bg-gray-500'
};

const GarageBookingPage = () => {
  const { slug } = useParams();
  const [garage, setGarage] = useState(null);
  const [bookingConfig, setBookingConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [bookingRef, setBookingRef] = useState('');
  const [bookingDetails, setBookingDetails] = useState(null);
  
  // Vehicle data
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [fuelTypes, setFuelTypes] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  
  // Form data
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    vehicle_brand: '',
    vehicle_model: '',
    vehicle_number: '',
    fuel_type: '',
    service_category: '',
    customer_complaint: '',
    preferred_date: null,
    time_slot: '',
    address: '',
    needs_pickup: false
  });

  useEffect(() => {
    fetchGarageData();
    fetchVehicleData();
  }, [slug]);

  const fetchGarageData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/garage-booking/public/${slug}`);
      const data = await res.json();
      
      if (data.success) {
        setGarage(data.garage);
        setBookingConfig(data.booking_page);
        setTimeSlots(data.time_slots || []);
      } else {
        toast({ title: 'Error', description: 'Garage not found', variant: 'destructive' });
      }
    } catch (error) {
      console.error('Error fetching garage:', error);
      toast({ title: 'Error', description: 'Failed to load garage information', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicleData = async () => {
    try {
      const [brandsRes, fuelRes] = await Promise.all([
        fetch(`${API_URL}/api/garage-booking/vehicles/brands`),
        fetch(`${API_URL}/api/garage-booking/fuel-types`)
      ]);
      
      const brandsData = await brandsRes.json();
      const fuelData = await fuelRes.json();
      
      if (brandsData.success) setBrands(brandsData.brands);
      if (fuelData.success) setFuelTypes(fuelData.fuel_types);
    } catch (error) {
      console.error('Error fetching vehicle data:', error);
    }
  };

  const fetchModels = async (brand) => {
    try {
      const res = await fetch(`${API_URL}/api/garage-booking/vehicles/models/${encodeURIComponent(brand)}`);
      const data = await res.json();
      if (data.success) setModels(data.models);
    } catch (error) {
      console.error('Error fetching models:', error);
    }
  };

  const handleBrandChange = (brand) => {
    setFormData({ ...formData, vehicle_brand: brand, vehicle_model: '' });
    fetchModels(brand);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.customer_phone || !formData.vehicle_brand || 
        !formData.service_category || !formData.preferred_date || !formData.time_slot) {
      toast({ title: 'Missing Fields', description: 'Please fill all required fields', variant: 'destructive' });
      return;
    }
    
    setSubmitting(true);
    
    try {
      const res = await fetch(`${API_URL}/api/garage-booking/public/${slug}/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          preferred_date: format(formData.preferred_date, 'yyyy-MM-dd')
        })
      });
      
      const data = await res.json();
      
      if (data.success) {
        setBookingRef(data.booking_reference);
        setBookingDetails(data.booking_details);
        setSubmitted(true);
        toast({ title: 'Booking Confirmed!', description: `Reference: ${data.booking_reference}` });
      } else {
        toast({ title: 'Error', description: data.detail || 'Failed to create booking', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to submit booking', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  // Share booking details
  const handleShare = async () => {
    const details = bookingDetails || {
      ref: bookingRef,
      customer_name: formData.customer_name,
      vehicle: `${formData.vehicle_brand} ${formData.vehicle_model}`,
      service: formData.service_category,
      date: format(formData.preferred_date, 'dd MMM yyyy'),
      time: formData.time_slot,
      garage_name: garage?.name
    };
    
    const shareText = `🚗 Booking Confirmed!\n\n` +
      `📋 Reference: ${details.ref}\n` +
      `👤 Name: ${details.customer_name}\n` +
      `🚘 Vehicle: ${details.vehicle}\n` +
      `🔧 Service: ${details.service}\n` +
      `📅 Date: ${details.date}\n` +
      `⏰ Time: ${details.time}\n` +
      `🏪 Garage: ${details.garage_name}\n` +
      (details.garage_phone ? `📞 Contact: ${details.garage_phone}\n` : '') +
      (details.pickup_required ? `📍 Pickup: Yes\n` : '') +
      `\nPowered by Propitstop Pitcrew`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `Booking ${details.ref}`,
          text: shareText
        });
      } catch (err) {
        // User cancelled or error - fall back to clipboard
        await copyToClipboard(shareText);
      }
    } else {
      await copyToClipboard(shareText);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: 'Copied!', description: 'Booking details copied to clipboard' });
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to copy', variant: 'destructive' });
    }
  };

  // Get available time slots based on date - requires 2 hour window for same-day bookings
  const getAvailableSlots = () => {
    if (!formData.preferred_date) return timeSlots;
    
    const now = new Date();
    const selectedDate = new Date(formData.preferred_date);
    const isToday = selectedDate.toDateString() === now.toDateString();
    
    if (!isToday) return timeSlots;
    
    // For same-day bookings, require at least 2 hours from now
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTimeIn24 = currentHour + (currentMinute / 60);
    const minBookingTime = currentTimeIn24 + 2; // 2-hour minimum window
    
    return timeSlots.filter(slot => {
      // Parse slot start time: "9:00 AM - 11:00 AM" -> extract "9:00 AM"
      const startTime = slot.split(' - ')[0].trim();
      const [time, period] = startTime.split(' ');
      const [hourStr, minuteStr] = time.split(':');
      let hour = parseInt(hourStr);
      
      // Convert to 24-hour format
      if (period === 'PM' && hour !== 12) hour += 12;
      if (period === 'AM' && hour === 12) hour = 0;
      
      const slotStartTime = hour + (parseInt(minuteStr || '0') / 60);
      
      return slotStartTime >= minBookingTime;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-orange-500 mx-auto" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!garage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Car className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h2 className="text-xl font-bold text-gray-800">Garage Not Found</h2>
            <p className="text-gray-600 mt-2">The booking page you're looking for doesn't exist.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Booking Confirmed!</h2>
            <p className="text-gray-600 mb-4">Your service appointment has been scheduled</p>
            
            <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-lg p-4 mb-6 text-white">
              <p className="text-sm opacity-90">Booking ID</p>
              <p className="text-3xl font-bold tracking-wider">{bookingRef}</p>
              <p className="text-xs opacity-75 mt-1">Save this for reference</p>
            </div>
            
            <div className="text-left space-y-2 mb-6 bg-gray-50 rounded-lg p-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Customer:</span>
                <span className="font-medium">{formData.customer_name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Vehicle:</span>
                <span className="font-medium">{formData.vehicle_brand} {formData.vehicle_model}</span>
              </div>
              {formData.vehicle_number && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Reg. No:</span>
                  <span className="font-medium">{formData.vehicle_number}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Service:</span>
                <span className="font-medium">{formData.service_category}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Date:</span>
                <span className="font-medium">{format(formData.preferred_date, 'dd MMM yyyy')}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Time:</span>
                <span className="font-medium">{formData.time_slot}</span>
              </div>
              {formData.needs_pickup && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Pickup:</span>
                  <span className="font-medium text-green-600">Yes - {formData.address}</span>
                </div>
              )}
            </div>
            
            {/* Share Button */}
            <Button 
              onClick={handleShare}
              className="w-full mb-4 bg-blue-500 hover:bg-blue-600"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share Booking Details
            </Button>
            
            <p className="text-sm text-gray-500">
              We will contact you at <strong>{formData.customer_phone}</strong> to confirm
            </p>
            
            <div className="mt-6 pt-4 border-t">
              <p className="text-xs text-gray-400">Powered by Propitstop Pitcrew</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const template = bookingConfig?.template || 'modern';
  const services = bookingConfig?.services || [];
  const heroImageUrl = bookingConfig?.hero_image_url || null;

  // Template-specific rendering
  if (template === 'modern') {
    return <ModernTemplate garage={garage} services={services} formData={formData} setFormData={setFormData} 
      brands={brands} models={models} fuelTypes={fuelTypes} timeSlots={getAvailableSlots()}
      handleBrandChange={handleBrandChange} handleSubmit={handleSubmit} submitting={submitting} heroImageUrl={heroImageUrl} />;
  }
  
  if (template === 'classic') {
    return <ClassicTemplate garage={garage} services={services} formData={formData} setFormData={setFormData}
      brands={brands} models={models} fuelTypes={fuelTypes} timeSlots={getAvailableSlots()}
      handleBrandChange={handleBrandChange} handleSubmit={handleSubmit} submitting={submitting} heroImageUrl={heroImageUrl} />;
  }
  
  if (template === 'minimal') {
    return <MinimalTemplate garage={garage} services={services} formData={formData} setFormData={setFormData}
      brands={brands} models={models} fuelTypes={fuelTypes} timeSlots={getAvailableSlots()}
      handleBrandChange={handleBrandChange} handleSubmit={handleSubmit} submitting={submitting} heroImageUrl={heroImageUrl} />;
  }

  // Default to modern
  return <ModernTemplate garage={garage} services={services} formData={formData} setFormData={setFormData}
    brands={brands} models={models} fuelTypes={fuelTypes} timeSlots={getAvailableSlots()}
    handleBrandChange={handleBrandChange} handleSubmit={handleSubmit} submitting={submitting} heroImageUrl={heroImageUrl} />;
};


// ============= MODERN TEMPLATE =============
const ModernTemplate = ({ garage, services, formData, setFormData, brands, models, fuelTypes, timeSlots, handleBrandChange, handleSubmit, submitting, heroImageUrl }) => {
  const heroUrl = heroImageUrl ? `${API_URL}/api${heroImageUrl}` : null;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Hero Banner */}
      {heroUrl ? (
        <div className="relative w-full h-48 md:h-64 lg:h-80 overflow-hidden">
          <img 
            src={heroUrl} 
            alt={`${garage.name} Banner`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4 md:p-8">
            <div className="max-w-4xl mx-auto flex items-center gap-4">
              {garage.logo_url ? (
                <img src={garage.logo_url.startsWith('/api') ? `${API_URL}${garage.logo_url}` : `${API_URL}/api${garage.logo_url}`} alt={garage.name} className="w-16 h-16 rounded-xl object-cover bg-white p-1 shadow-lg" />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                  <Car className="w-8 h-8 text-white" />
                </div>
              )}
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-white drop-shadow-lg">{garage.name}</h1>
                <p className="text-white/80 text-sm flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> {garage.city}
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Default Header - gradient when no hero image */
        <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white">
          <div className="max-w-4xl mx-auto px-4 py-8">
            <div className="flex items-center gap-4">
              {garage.logo_url ? (
                <img src={garage.logo_url.startsWith('/api') ? `${API_URL}${garage.logo_url}` : `${API_URL}/api${garage.logo_url}`} alt={garage.name} className="w-16 h-16 rounded-xl object-cover bg-white p-1" />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center">
                  <Car className="w-8 h-8" />
                </div>
              )}
              <div>
                <h1 className="text-2xl md:text-3xl font-bold">{garage.name}</h1>
                <p className="text-white/80 text-sm flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> {garage.city}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Service Selection */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Wrench className="w-5 h-5 text-orange-500" />
                Select Service
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {services.map((service) => {
                  const Icon = SERVICE_ICONS[service.category] || Wrench;
                  const isSelected = formData.service_category === service.name;
                  return (
                    <button
                      key={service.id}
                      type="button"
                      onClick={() => setFormData({ ...formData, service_category: service.name })}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        isSelected 
                          ? 'border-orange-500 bg-orange-500/20' 
                          : 'border-slate-600 bg-slate-700/50 hover:border-slate-500'
                      }`}
                    >
                      <Icon className={`w-6 h-6 mb-2 ${isSelected ? 'text-orange-500' : 'text-slate-400'}`} />
                      <p className={`font-medium text-sm ${isSelected ? 'text-white' : 'text-slate-300'}`}>
                        {service.name}
                      </p>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Vehicle Details */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Car className="w-5 h-5 text-orange-500" />
                Vehicle Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Brand *</Label>
                  <Select value={formData.vehicle_brand} onValueChange={handleBrandChange}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                      <SelectValue placeholder="Select brand" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {brands.map(brand => (
                        <SelectItem key={brand} value={brand} className="text-white">{brand}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">Model</Label>
                  <Select value={formData.vehicle_model} onValueChange={(v) => setFormData({...formData, vehicle_model: v})}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                      <SelectValue placeholder="Select model" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {models.map(model => (
                        <SelectItem key={model} value={model} className="text-white">{model}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Fuel Type *</Label>
                  <Select value={formData.fuel_type} onValueChange={(v) => setFormData({...formData, fuel_type: v})}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                      <SelectValue placeholder="Select fuel" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {fuelTypes.map(fuel => (
                        <SelectItem key={fuel} value={fuel} className="text-white">{fuel}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">Vehicle Number</Label>
                  <Input 
                    value={formData.vehicle_number}
                    onChange={(e) => setFormData({...formData, vehicle_number: e.target.value.toUpperCase()})}
                    placeholder="KA01AB1234"
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Customer Details */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <User className="w-5 h-5 text-orange-500" />
                Your Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Name *</Label>
                  <Input 
                    value={formData.customer_name}
                    onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                    placeholder="Your name"
                    className="bg-slate-700 border-slate-600 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Phone *</Label>
                  <Input 
                    value={formData.customer_phone}
                    onChange={(e) => setFormData({...formData, customer_phone: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                    placeholder="9876543210"
                    className="bg-slate-700 border-slate-600 text-white"
                    required
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Email (Optional)</Label>
                <Input 
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                  placeholder="your@email.com"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">Describe your issue / complaint</Label>
                <Textarea 
                  value={formData.customer_complaint}
                  onChange={(e) => setFormData({...formData, customer_complaint: e.target.value})}
                  placeholder="Tell us about your vehicle's issue..."
                  className="bg-slate-700 border-slate-600 text-white"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Schedule */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-orange-500" />
                Preferred Schedule
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start bg-slate-700 border-slate-600 text-white">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.preferred_date ? format(formData.preferred_date, 'PPP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-slate-800 border-slate-700">
                      <Calendar
                        mode="single"
                        selected={formData.preferred_date}
                        onSelect={(date) => setFormData({...formData, preferred_date: date, time_slot: ''})}
                        disabled={(date) => date < new Date()}
                        className="text-white"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="text-slate-300">Time Slot *</Label>
                  <Select value={formData.time_slot} onValueChange={(v) => setFormData({...formData, time_slot: v})}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {timeSlots.length > 0 ? (
                        timeSlots.map(slot => (
                          <SelectItem key={slot} value={slot} className="text-white">{slot}</SelectItem>
                        ))
                      ) : (
                        <SelectItem value="none" disabled className="text-slate-400">No slots available today</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  {formData.preferred_date && new Date(formData.preferred_date).toDateString() === new Date().toDateString() && timeSlots.length === 0 && (
                    <p className="text-xs text-orange-400 mt-1">Same-day bookings require 2 hours notice. Please select tomorrow.</p>
                  )}
                </div>
              </div>
              
              {/* Pickup Option */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-700/50 border border-slate-600">
                <input
                  type="checkbox"
                  id="needs_pickup"
                  checked={formData.needs_pickup}
                  onChange={(e) => setFormData({...formData, needs_pickup: e.target.checked, address: e.target.checked ? formData.address : ''})}
                  className="w-5 h-5 rounded border-slate-500 bg-slate-600 text-orange-500 focus:ring-orange-500"
                />
                <label htmlFor="needs_pickup" className="text-slate-300 cursor-pointer">
                  I need vehicle pickup from my location
                </label>
              </div>
              
              {formData.needs_pickup && (
                <div>
                  <Label className="text-slate-300">Pickup Address *</Label>
                  <Textarea 
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="Enter your complete pickup address"
                    className="bg-slate-700 border-slate-600 text-white"
                    rows={2}
                    required
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Submit Button */}
          <Button 
            type="submit" 
            disabled={submitting}
            className="w-full h-14 text-lg bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
          >
            {submitting ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Booking...</>
            ) : (
              <>Book Service <ArrowRight className="w-5 h-5 ml-2" /></>
            )}
          </Button>
        </form>

        {/* Footer */}
        <div className="text-center mt-8 py-4 border-t border-slate-700">
          <p className="text-slate-500 text-sm">
            Powered by <span className="text-orange-500 font-semibold">Propitstop Pitcrew</span>
          </p>
        </div>
      </div>
    </div>
  );
};


// ============= CLASSIC TEMPLATE =============
const ClassicTemplate = ({ garage, services, formData, setFormData, brands, models, fuelTypes, timeSlots, handleBrandChange, handleSubmit, submitting, heroImageUrl }) => {
  const heroUrl = heroImageUrl ? `${API_URL}/api${heroImageUrl}` : null;
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Banner */}
      {heroUrl ? (
        <div className="relative w-full h-40 md:h-56 overflow-hidden">
          <img 
            src={heroUrl} 
            alt={`${garage.name} Banner`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-white via-white/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <div className="max-w-3xl mx-auto flex items-center gap-4">
              {garage.logo_url ? (
                <img src={garage.logo_url.startsWith('/api') ? `${API_URL}${garage.logo_url}` : `${API_URL}/api${garage.logo_url}`} alt={garage.name} className="w-14 h-14 rounded-lg object-cover shadow-lg border-2 border-white" />
              ) : (
                <div className="w-14 h-14 rounded-lg bg-orange-100 flex items-center justify-center shadow-lg border-2 border-white">
                  <Car className="w-7 h-7 text-orange-600" />
                </div>
              )}
              <div>
                <h1 className="text-xl font-bold text-gray-900 drop-shadow">{garage.name}</h1>
                <p className="text-gray-600 text-sm">{garage.address}, {garage.city}</p>
                <p className="text-orange-600 text-sm font-medium">{garage.mobile}</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Default Header */
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-3xl mx-auto px-4 py-6">
            <div className="flex items-center gap-4">
              {garage.logo_url ? (
                <img src={garage.logo_url.startsWith('/api') ? `${API_URL}${garage.logo_url}` : `${API_URL}/api${garage.logo_url}`} alt={garage.name} className="w-14 h-14 rounded-lg object-cover" />
              ) : (
                <div className="w-14 h-14 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Car className="w-7 h-7 text-orange-600" />
                </div>
              )}
              <div>
                <h1 className="text-xl font-bold text-gray-900">{garage.name}</h1>
                <p className="text-gray-500 text-sm">{garage.address}, {garage.city}</p>
                <p className="text-orange-600 text-sm font-medium">{garage.mobile}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto px-4 py-8">
        <Card className="shadow-lg">
          <CardHeader className="bg-orange-50 border-b">
            <CardTitle className="text-gray-800">Book Your Service</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Service Selection */}
              <div>
                <Label className="text-gray-700 font-semibold mb-3 block">Select Service *</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {services.map((service) => {
                    const isSelected = formData.service_category === service.name;
                    return (
                      <button
                        key={service.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, service_category: service.name })}
                        className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                          isSelected 
                            ? 'border-orange-500 bg-orange-50 text-orange-700' 
                            : 'border-gray-200 hover:border-gray-300 text-gray-700'
                        }`}
                      >
                        {service.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Customer Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-700">Your Name *</Label>
                  <Input 
                    value={formData.customer_name}
                    onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                    placeholder="Full name"
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Phone Number *</Label>
                  <Input 
                    value={formData.customer_phone}
                    onChange={(e) => setFormData({...formData, customer_phone: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                    placeholder="9876543210"
                    className="mt-1"
                    required
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-gray-700">Email (Optional)</Label>
                <Input 
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                  placeholder="your@email.com"
                  className="mt-1"
                />
              </div>

              {/* Vehicle Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-700">Vehicle Brand *</Label>
                  <Select value={formData.vehicle_brand} onValueChange={handleBrandChange}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Select brand" /></SelectTrigger>
                    <SelectContent>
                      {brands.map(brand => (<SelectItem key={brand} value={brand}>{brand}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-700">Vehicle Model</Label>
                  <Select value={formData.vehicle_model} onValueChange={(v) => setFormData({...formData, vehicle_model: v})}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Select model" /></SelectTrigger>
                    <SelectContent>
                      {models.map(model => (<SelectItem key={model} value={model}>{model}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-700">Fuel Type *</Label>
                  <Select value={formData.fuel_type} onValueChange={(v) => setFormData({...formData, fuel_type: v})}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Select fuel" /></SelectTrigger>
                    <SelectContent>
                      {fuelTypes.map(fuel => (<SelectItem key={fuel} value={fuel}>{fuel}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-700">Vehicle Number</Label>
                  <Input 
                    value={formData.vehicle_number}
                    onChange={(e) => setFormData({...formData, vehicle_number: e.target.value.toUpperCase()})}
                    placeholder="KA01AB1234"
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Schedule */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-700">Preferred Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start mt-1">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.preferred_date ? format(formData.preferred_date, 'PPP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.preferred_date}
                        onSelect={(date) => setFormData({...formData, preferred_date: date, time_slot: ''})}
                        disabled={(date) => date < new Date()}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="text-gray-700">Time Slot *</Label>
                  <Select value={formData.time_slot} onValueChange={(v) => setFormData({...formData, time_slot: v})}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Select time" /></SelectTrigger>
                    <SelectContent>
                      {timeSlots.length > 0 ? (
                        timeSlots.map(slot => (<SelectItem key={slot} value={slot}>{slot}</SelectItem>))
                      ) : (
                        <SelectItem value="none" disabled>No slots available today</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  {formData.preferred_date && new Date(formData.preferred_date).toDateString() === new Date().toDateString() && timeSlots.length === 0 && (
                    <p className="text-xs text-orange-600 mt-1">Same-day bookings require 2 hours notice. Please select tomorrow.</p>
                  )}
                </div>
              </div>
              
              {/* Pickup Option */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-orange-50 border border-orange-200">
                <input
                  type="checkbox"
                  id="needs_pickup_classic"
                  checked={formData.needs_pickup}
                  onChange={(e) => setFormData({...formData, needs_pickup: e.target.checked, address: e.target.checked ? formData.address : ''})}
                  className="w-5 h-5 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                <label htmlFor="needs_pickup_classic" className="text-gray-700 cursor-pointer">
                  I need vehicle pickup from my location
                </label>
              </div>
              
              {formData.needs_pickup && (
                <div>
                  <Label className="text-gray-700">Pickup Address *</Label>
                  <Textarea 
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="Enter your complete pickup address"
                    className="mt-1"
                    rows={2}
                    required
                  />
                </div>
              )}

              {/* Complaint */}
              <div>
                <Label className="text-gray-700">Describe your issue</Label>
                <Textarea 
                  value={formData.customer_complaint}
                  onChange={(e) => setFormData({...formData, customer_complaint: e.target.value})}
                  placeholder="What's wrong with your vehicle?"
                  className="mt-1"
                  rows={3}
                />
              </div>

              <Button type="submit" disabled={submitting} className="w-full h-12 bg-orange-600 hover:bg-orange-700">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Book Appointment'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-gray-400 text-sm mt-6">
          Powered by <span className="text-orange-500">Propitstop Pitcrew</span>
        </p>
      </div>
    </div>
  );
};


// ============= MINIMAL TEMPLATE =============
const MinimalTemplate = ({ garage, services, formData, setFormData, brands, models, fuelTypes, timeSlots, handleBrandChange, handleSubmit, submitting, heroImageUrl }) => {
  const heroUrl = heroImageUrl ? `${API_URL}/api${heroImageUrl}` : null;
  
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Banner */}
      {heroUrl && (
        <div className="relative w-full h-32 md:h-48 overflow-hidden">
          <img 
            src={heroUrl} 
            alt={`${garage.name} Banner`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-white via-white/20 to-transparent" />
        </div>
      )}
      
      <div className="max-w-xl mx-auto px-4 py-12">
        {/* Header */}
        <div className={`text-center ${heroUrl ? '-mt-16 relative z-10' : ''} mb-10`}>
          {garage.logo_url ? (
            <img 
              src={garage.logo_url.startsWith('/api') ? `${API_URL}${garage.logo_url}` : `${API_URL}/api${garage.logo_url}`} 
              alt={garage.name} 
              className={`w-20 h-20 rounded-full object-cover mx-auto mb-4 ${heroUrl ? 'border-4 border-white shadow-lg' : ''}`}
            />
          ) : (
            <div className={`w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4 ${heroUrl ? 'border-4 border-white shadow-lg' : ''}`}>
              <Car className="w-10 h-10 text-gray-400" />
            </div>
          )}
          <h1 className="text-2xl font-bold text-gray-900">{garage.name}</h1>
          <p className="text-gray-500">{garage.city}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Service */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-3">What do you need?</label>
            <div className="flex flex-wrap gap-2">
              {services.map((service) => (
                <Badge
                  key={service.id}
                  variant={formData.service_category === service.name ? "default" : "outline"}
                  className={`cursor-pointer px-4 py-2 text-sm ${
                    formData.service_category === service.name ? 'bg-black text-white' : 'hover:bg-gray-100'
                  }`}
                  onClick={() => setFormData({ ...formData, service_category: service.name })}
                >
                  {service.name}
                </Badge>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <Input 
              value={formData.customer_name}
              onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
              placeholder="Your name"
              className="h-12 border-0 border-b border-gray-200 rounded-none focus:ring-0 focus:border-black"
              required
            />
            <Input 
              value={formData.customer_phone}
              onChange={(e) => setFormData({...formData, customer_phone: e.target.value.replace(/\D/g, '').slice(0, 10)})}
              placeholder="Phone number"
              className="h-12 border-0 border-b border-gray-200 rounded-none focus:ring-0 focus:border-black"
              required
            />
            <Input 
              type="email"
              value={formData.customer_email}
              onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
              placeholder="Email (optional)"
              className="h-12 border-0 border-b border-gray-200 rounded-none focus:ring-0 focus:border-black"
            />
          </div>

          {/* Vehicle */}
          <div className="grid grid-cols-2 gap-4">
            <Select value={formData.vehicle_brand} onValueChange={handleBrandChange}>
              <SelectTrigger className="h-12 border-0 border-b border-gray-200 rounded-none">
                <SelectValue placeholder="Brand" />
              </SelectTrigger>
              <SelectContent>{brands.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={formData.fuel_type} onValueChange={(v) => setFormData({...formData, fuel_type: v})}>
              <SelectTrigger className="h-12 border-0 border-b border-gray-200 rounded-none">
                <SelectValue placeholder="Fuel" />
              </SelectTrigger>
              <SelectContent>{fuelTypes.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {/* Schedule */}
          <div className="grid grid-cols-2 gap-4">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" className="h-12 justify-start border-0 border-b border-gray-200 rounded-none">
                  {formData.preferred_date ? format(formData.preferred_date, 'dd MMM') : 'Date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={formData.preferred_date}
                  onSelect={(date) => setFormData({...formData, preferred_date: date, time_slot: ''})}
                  disabled={(date) => date < new Date()}
                />
              </PopoverContent>
            </Popover>
            <Select value={formData.time_slot} onValueChange={(v) => setFormData({...formData, time_slot: v})}>
              <SelectTrigger className="h-12 border-0 border-b border-gray-200 rounded-none">
                <SelectValue placeholder="Time" />
              </SelectTrigger>
              <SelectContent>
                {timeSlots.length > 0 ? (
                  timeSlots.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)
                ) : (
                  <SelectItem value="none" disabled>No slots today</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          {formData.preferred_date && new Date(formData.preferred_date).toDateString() === new Date().toDateString() && timeSlots.length === 0 && (
            <p className="text-xs text-orange-600">Same-day bookings require 2 hours notice. Please select tomorrow.</p>
          )}
          
          {/* Pickup Option */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="needs_pickup_minimal"
              checked={formData.needs_pickup}
              onChange={(e) => setFormData({...formData, needs_pickup: e.target.checked, address: e.target.checked ? formData.address : ''})}
              className="w-5 h-5 rounded border-gray-300 text-black focus:ring-black"
            />
            <label htmlFor="needs_pickup_minimal" className="text-gray-600 cursor-pointer text-sm">
              I need vehicle pickup
            </label>
          </div>
          
          {formData.needs_pickup && (
            <Input 
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              placeholder="Enter pickup address"
              className="h-12 border-0 border-b border-gray-200 rounded-none focus:ring-0 focus:border-black"
              required
            />
          )}

          {/* Issue */}
          <Textarea 
            value={formData.customer_complaint}
            onChange={(e) => setFormData({...formData, customer_complaint: e.target.value})}
            placeholder="Describe your issue (optional)"
            className="border-0 border-b border-gray-200 rounded-none resize-none focus:ring-0"
            rows={2}
          />

          <Button type="submit" disabled={submitting} className="w-full h-14 bg-black hover:bg-gray-900 rounded-full">
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Book Now'}
          </Button>
        </form>

        <p className="text-center text-gray-300 text-xs mt-10">
          powered by propitstop pitcrew
        </p>
      </div>
    </div>
  );
};

export default GarageBookingPage;
